import useCampaigns from '../useCampaigns';
import { isNumber } from 'utility/utility';
import moment from 'moment';
import uniq from 'lodash/uniq';
import { useMemo } from 'react';
import { useInstagramAudios, useInstagramTimeSeries } from '@round/platforms/instagram';
import usePublicReportId from '../../usePublicReportId';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { InstagramPublicReportCampaignTableRow } from '../CampaignsTable/InstagramPublicReportCampaignTable';

export function useInstagramCampaigns() {
    const publicReportId = usePublicReportId();
    const { getCurrencyById } = useCurrencies();
    const { campaigns, ...campaignsHookData } = useCampaigns('instagram');
    const { campaignStatsData, projectsData, brandsData, teamsData } = campaignsHookData;

    const audioIds = uniq(campaigns.map((c) => c.instagram_details?.audio_id).filter(isNumber));

    const { data: audiosData } = useInstagramAudios(
        { id: audioIds.join(','), page_size: audioIds.length, publicReportId },
        {
            enabled: audioIds.length > 0,
            select: (data) => new Map(data.results.map((a) => [a.id, a])),
        }
    );

    const {
        data: timeSeriesData,
        isLoading: isTimeSeriesLoading,
        isError: isTimeSeriesError,
    } = useInstagramTimeSeries(
        {
            type: 'audio',
            ids: audioIds,
            fields: ['posts_daily_change'],
            start_date: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
            publicReportId,
        },
        { enabled: audioIds.length > 0 }
    );

    const rows = useMemo(
        () =>
            campaigns.map<InstagramPublicReportCampaignTableRow>((c) => {
                const stats = campaignStatsData[c.id]?.data ?? null;
                const project = projectsData.get(c.project_id) ?? null;
                const brand = project ? brandsData[project.brand_id]?.data ?? null : null;
                const team = project ? teamsData.get(project.team_id) ?? null : null;
                const audioId = c.instagram_details?.audio_id;
                const audio = audioId ? audiosData?.get(audioId) ?? null : null;
                const audioTimeSeries = audioId ? timeSeriesData?.[audioId] ?? null : null;
                const currency = getCurrencyById(c.currency_id);

                return { ...c, project, brand, team, stats, audio, audioTimeSeries, currency };
            }),
        [brandsData, campaignStatsData, campaigns, audiosData, timeSeriesData, projectsData, teamsData, getCurrencyById]
    );

    return {
        rows,
        campaigns,
        timeSeriesData,
        audiosData,
        ...campaignsHookData,
        isTimeSeriesLoading,
        isTimeSeriesError,
    };
}
